import { Component, Input, Output, EventEmitter, OnInit } from '@angular/core';
import { ImageEditorDialogComponent } from '../image-editor/image-editor-dialog';
import { MatDialog } from '@angular/material';
import { environment } from '@env/environment';
import { ConfirmationDialogComponent } from '../../../../confirmation-dialog/confirmation-dialog.component';

@Component({
  selector: 'app-image-control',
  templateUrl: './image-control.component.html',
  styleUrls: ['./image-control.component.scss']
})
export class ImageControlComponent implements OnInit {
  // Inputs
  @Input() label: string = '';
  @Input() imageSrc: string = ''; // current image URL
  @Input() controlType: string; // 'toggle' or 'slider'
  @Input() controlValue: any;   // boolean for toggle, number for slider
  // Local asset shipped with the admin build. Must stay in sync with the
  // '/assets/images/image-not-found-150x150.png' literal used by the call sites that bind
  // [imageSrc], because the template hides the delete button when imageSrc === fallbackSrc.
  @Input() fallbackSrc: string = '/assets/images/image-not-found-150x150.png';
  @Input() min: number = 0.5;
  @Input() max: number = 2;
  @Input() step: number = 0.1;
  @Input() hideUpload: boolean = false;
  @Input() hideEdit: boolean = false;
  @Input() useFixedGrid: boolean = false;
  @Input() showDeleteIcon: boolean = false;

  // Outputs
  @Output() controlChange = new EventEmitter<any>();
  @Output() fileUpload = new EventEmitter<File>();
  @Output() imageRemoved = new EventEmitter<void>();

  constructor(public dialog: MatDialog) {
    // Set default values for older TS versions
    if (!this.controlType) {
      this.controlType = 'toggle';
    }
    if (this.controlValue === undefined) {
      this.controlValue = this.controlType === 'toggle' ? true : 1;
    }
  }

  ngOnInit() {
    // In case inputs are passed after initialization
    if (!this.controlType) {
      this.controlType = 'toggle';
    }
    if (this.controlValue === undefined) {
      this.controlValue = this.controlType === 'toggle' ? true : 1;
    }
  }

  // Non-re-entrant: see the note on GameSettingsComponent.onImageError. Without the guard
  // a fallback that cannot itself load re-triggers `error` on every re-assignment.
  onImgError(event: Event) {
    const img = event.target as HTMLImageElement;
    if (img.getAttribute('src') === this.fallbackSrc) {
      return;
    }
    img.src = this.fallbackSrc;
  }

  onFileChange(event: Event) {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      const file = input.files[0];
      this.fileUpload.emit(file);
    }
  }

  onRemove(event?: Event) {
    if (event) {
      event.stopPropagation();
      event.preventDefault();
    }
    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      disableClose: false,
      data: {
        message: "Are you sure you want to Delete?",
        isConfirmation: true
      }
    });

    dialogRef.afterClosed().subscribe((result: boolean) => {
      if (result) {
        this.imageRemoved.emit();
      }
    });
  }

  onToggleChange(event: any) {
    this.controlValue = event.checked;
    this.controlChange.emit(this.controlValue);
  }

  onSliderChange(value: number) {
    this.controlValue = value;
    this.controlChange.emit(this.controlValue);
  }

  getTristateState(): 1 | 2 | 3 {
    if (this.controlValue === 'medium' || this.controlValue == 2) {
      return 2;
    }
    if (this.controlValue === 'large' || this.controlValue == 3) {
      return 3;
    }
    return 1;
  }

  cycleTristate() {
    const currentState = this.getTristateState();
    let emittedValue: any;

    if (currentState === 1) {
      emittedValue = typeof this.controlValue === 'string' ? 'medium' : 2;
    } else if (currentState === 2) {
      emittedValue = typeof this.controlValue === 'string' ? 'large' : 3;
    } else {
      emittedValue = typeof this.controlValue === 'string' ? 'small' : 1;
    }

    this.controlValue = emittedValue;
    this.controlChange.emit(this.controlValue);
  }

  openEditor() {
    // Open image editor dialog
    this.dialog.open(ImageEditorDialogComponent, {
      width: '900px',
      panelClass: 'image-editor-dialog',
      data: { source: this.resolveImageSrc(this.imageSrc) }
    }).afterClosed().subscribe(res => {
      if (res && res.blob) {
        const type = res.blob.type || 'application/octet-stream';
        let name = 'edited_image.bin';
        if (type === 'image/png') name = 'edited_image.png';
        else if (type === 'image/jpeg') name = 'edited_image.jpg';
        else if (type === 'image/webp') name = 'edited_image.webp';
        const file = new File([res.blob], name, { type });
        this.fileUpload.emit(file);
      }
    });
  }

  private resolveImageSrc(src: any): string {
    if (!src) return this.fallbackSrc;
    const v = String((src && (src.changingThisBreaksApplicationSecurity || src)) || '').trim();
    if (/^(data:|https?:|blob:)/i.test(v)) return v;
    if (v.startsWith('/assets/')) return v;
    const cleaned = v.replace(environment.s3BucketUrl, '').replace(/^\/+/, '');
    return environment.s3BucketUrl + cleaned;
  }
}
